import React from 'react';

import {MixtureSchemaConfig} from 'constants/RegistryConstants';
import HelpButton from 'help/HelpButton';
import {ORDERED_MIXTURE_UNITS_FOR_DISPLAY} from 'registry/mixtures/MixtureConstants';
import FormulaIcon from 'ui-library/icons/FormulaIcon';

export default function IngredientTableTitle({
  tagSchemaName,
  mixtureSchemaConfig,
}: {
  tagSchemaName: string;
  mixtureSchemaConfig: MixtureSchemaConfig;
}): JSX.Element {
  const sectionsInCommon = {
    component: (
      <>
        <div>
          <b>Component</b>
        </div>
        <div>Link to any entity representing an ingredient.</div>
      </>
    ),
    catalogIdentifier: (
      <>
        <div>
          <b>Catalog #</b>
        </div>
        <div>Identifier for the component entity, such as manufacturer ID.</div>
      </>
    ),
    units: (
      <>
        <div>
          <b>Ingredient Units</b>
        </div>
        <div>Units that will be used to measure an ingredient.</div>
      </>
    ),
    notes: (
      <>
        <div>
          <b>Notes</b>
        </div>
        <div>Any additional comments or notes about the ingredient.</div>
      </>
    ),
  };

  function getPrepHelp(mixtureSchemaConfig: MixtureSchemaConfig): JSX.Element {
    return (
      <>
        <div className="u-small-spacing-bottom">
          <div>
            <b>Target Amount</b>
          </div>
          <div>The total quantity of the mixture you intend to create.</div>
        </div>
        <div className="u-small-spacing-bottom">{sectionsInCommon.component}</div>
        <div className="u-small-spacing-bottom">{sectionsInCommon.catalogIdentifier}</div>
        <div className="u-small-spacing-bottom">
          <div>
            <b>
              Target Amount <FormulaIcon className="u-faded u-normal-font-weight u-no-text-transform" />
            </b>
          </div>
          <div>
            The scaled quantity for an inherited ingredient, based on the mixture's target amount. This is
            blank if the ingredient is not inherited.
          </div>
        </div>
        <div className="u-small-spacing-bottom">{sectionsInCommon.units}</div>
        <div className="u-small-spacing-bottom">
          <div>
            <b>Measured Amount</b>
          </div>
          <div>Record the actual quantity of an ingredient measured when preparing the mixture.</div>
        </div>
        {mixtureSchemaConfig.componentLotTextEnabled && (
          <div className="u-small-spacing-bottom">
            <div>
              <b>Lot Text</b>
            </div>
            <div>Identifier for the specific material used in this preparation.</div>
          </div>
        )}
        {mixtureSchemaConfig.componentLotStorageEnabled && (
          <>
            <div className="u-small-spacing-bottom">
              <div>
                <b>Lot Container</b>
              </div>
              <div>Holds the specific entity used in this preparation.</div>
            </div>
            <div className="u-small-spacing-bottom">
              <div>
                <b>Lot Entity</b>
              </div>
              <div>The specific entity used in this preparation.</div>
            </div>
          </>
        )}
        <div>{sectionsInCommon.notes}</div>
      </>
    );
  }

  function getRecipeHelp(): JSX.Element {
    return (
      <>
        <div className="u-small-spacing-bottom">
          <div>
            <b>Ingredients specified</b>
          </div>
          <div>
            Set ingredients to be per {ORDERED_MIXTURE_UNITS_FOR_DISPLAY.slice(0, -1).join(', ')}, or{' '}
            {ORDERED_MIXTURE_UNITS_FOR_DISPLAY.slice(-1)[0]}. This is reflected in the ingredient units.
          </div>
        </div>
        <div className="u-small-spacing-bottom">{sectionsInCommon.component}</div>
        <div className="u-small-spacing-bottom">{sectionsInCommon.catalogIdentifier}</div>
        <div className="u-small-spacing-bottom">
          <div>
            <b>Target Amount</b>
          </div>
          <div>Quantity of ingredient a scientist should aim to measure out.</div>
        </div>
        <div className="u-small-spacing-bottom">{sectionsInCommon.units}</div>
        <div>{sectionsInCommon.notes}</div>
      </>
    );
  }

  return (
    <>
      {`${tagSchemaName} Ingredients `}
      <HelpButton
        renderContent={() => (
          <>
            {mixtureSchemaConfig.allowMeasuredIngredients
              ? getPrepHelp(mixtureSchemaConfig)
              : getRecipeHelp()}
          </>
        )}
      />
    </>
  );
}
